// Fatigue Detection — Detects cognitive fatigue via RT slope analysis
// Compares recent response times against the session's early baseline.
// If RT increases significantly (> threshold stddevs above mean), flags fatigue.

import {
  FATIGUE_MIN_POSITION, FATIGUE_WINDOW_SIZE,
  FATIGUE_SHIFT_THRESHOLD, FATIGUE_END_THRESHOLD
} from './constants.js';

// Import session state reader
import { getSessionState } from './session-planner.js';

/**
 * Build fatigue baseline from historical sessions.
 * Stores per-user baseline RT statistics.
 * For now, baseline is computed from the first N answers of the current session.
 *
 * @param {string} userId
 * @returns {object|null} baseline stats
 */
export async function buildBaseline(userId) {
  // Phase 3: baseline is intra-session (first FATIGUE_MIN_POSITION answers)
  // Future: cross-session baseline from Firebase
  return null;
}

/**
 * Check for fatigue in the current session.
 * Compares recent RT window against early-session baseline.
 *
 * @param {string} userId
 * @param {string} sessionToken
 * @returns {null|object} null (no fatigue) or { type, severity, recommendedAction, detail }
 */
export async function checkFatigue(userId, sessionToken) {
  const session = getSessionState(sessionToken);
  if (!session || !session.answers) return null;

  const answers = session.answers;

  // Need enough data: at least FATIGUE_MIN_POSITION answers
  if (answers.length < FATIGUE_MIN_POSITION) return null;

  // Extract response times (filter out 0/missing)
  const rts = answers
    .map(a => a.responseMs)
    .filter(rt => rt && rt > 0);

  if (rts.length < FATIGUE_MIN_POSITION) return null;

  // Baseline: first half of answers (or first FATIGUE_MIN_POSITION)
  const baselineSize = Math.max(FATIGUE_MIN_POSITION, Math.floor(rts.length / 2));
  const baselineRTs = rts.slice(0, Math.min(baselineSize, rts.length));

  // Recent window: last FATIGUE_WINDOW_SIZE answers
  const windowSize = Math.min(FATIGUE_WINDOW_SIZE, Math.floor(rts.length / 2));
  if (windowSize < 2) return null;
  const recentRTs = rts.slice(-windowSize);

  // Compute baseline stats
  const baselineMean = mean(baselineRTs);
  const baselineStd = stddev(baselineRTs, baselineMean);

  if (baselineMean === 0 || baselineStd === 0) return null;

  // Compute recent mean
  const recentMean = mean(recentRTs);

  // Z-score: how many stddevs above baseline mean?
  const zScore = (recentMean - baselineMean) / baselineStd;

  // Check thresholds
  if (zScore >= FATIGUE_END_THRESHOLD) {
    return {
      type: 'fatigue',
      severity: 'high',
      recommendedAction: 'end_session',
      detail: {
        zScore: Math.round(zScore * 100) / 100,
        baselineMean: Math.round(baselineMean),
        recentMean: Math.round(recentMean),
        rtIncrease: Math.round((recentMean - baselineMean) / baselineMean * 100) + '%'
      }
    };
  }

  if (zScore >= FATIGUE_SHIFT_THRESHOLD) {
    return {
      type: 'fatigue',
      severity: 'moderate',
      recommendedAction: 'suggest_break',
      detail: {
        zScore: Math.round(zScore * 100) / 100,
        baselineMean: Math.round(baselineMean),
        recentMean: Math.round(recentMean),
        rtIncrease: Math.round((recentMean - baselineMean) / baselineMean * 100) + '%'
      }
    };
  }

  return null;
}

// ── Math helpers ──

function mean(arr) {
  if (arr.length === 0) return 0;
  return arr.reduce((sum, v) => sum + v, 0) / arr.length;
}

function stddev(arr, avg) {
  if (arr.length < 2) return 0;
  const m = avg !== undefined ? avg : mean(arr);
  const variance = arr.reduce((sum, v) => sum + (v - m) ** 2, 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

// Export for testing
export { mean, stddev };
